import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ArticleSchema, BreadcrumbSchema } from '@/components/feature/SchemaOrg';
import useSEO from '@/hooks/useSEO';
import { articleDetails } from '@/mocks/article';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = id ? articleDetails[id] : null;

  const seoTitle = article ? article.title : '文章未找到';
  const seoDesc = article
    ? (article.content.find((b) => b.type === 'paragraph')?.text?.slice(0, 150) || article.title)
    : '文章未找到';
  const seoKeywords = article ? article.tags.join(', ') : '';
  const seoCanonical = article ? `https://taokep.cn/article/${article.id}` : undefined;

  useSEO({
    title: seoTitle,
    description: seoDesc,
    keywords: seoKeywords,
    canonicalUrl: seoCanonical,
    ogType: 'article',
  });

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <p className="text-lg text-foreground-600 mb-4">文章未找到</p>
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-primary-600 font-medium cursor-pointer">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { name: '首页', url: 'https://taokep.cn/' },
    { name: article.category, url: `https://taokep.cn/category/${article.categorySlug}` },
    { name: article.title, url: `https://taokep.cn/article/${article.id}` },
  ];

  return (
    <>
      <ArticleSchema
        headline={article.title}
        description={seoDesc}
        url={`https://taokep.cn/article/${article.id}`}
        image={article.image}
        datePublished={article.date}
        dateModified={article.date}
        authorName={article.author}
      />
      <BreadcrumbSchema items={breadcrumbItems} />

      <Navbar />
      <main>
        <nav className="bg-background-50 border-b border-background-200/70" aria-label="Breadcrumb">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-900 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </div>
        </nav>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-400">阅读 {article.readTime}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-6">
                {article.title}
              </h1>
              <div className="flex items-center gap-3">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover border-2 border-background-50"
                />
                <div>
                  <div className="text-sm font-medium text-foreground-900">{article.author}</div>
                  <div className="text-xs text-foreground-500">资深编辑</div>
                </div>
              </div>
            </header>

            <div className="aspect-[16/8] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="max-w-none">
              {article.content.map((block, index) => {
                if (block.type === 'heading' && block.text) {
                  return (
                    <h2 key={index} className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-10 mb-4">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'paragraph' && block.text) {
                  return (
                    <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === 'image' && block.src) {
                  return (
                    <figure key={index} className="my-8">
                      <div className="rounded-xl overflow-hidden">
                        <img
                          src={block.src}
                          alt={block.alt || ''}
                          className="w-full object-cover"
                        />
                      </div>
                      {block.alt && (
                        <figcaption className="text-center text-xs text-foreground-400 mt-3">
                          {block.alt}
                        </figcaption>
                      )}
                    </figure>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-accent-50 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>

        {article.relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {article.relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group overflow-hidden rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-foreground-500 mb-2">{related.date}</p>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}